// import '@ssap/ssap-ui-kit/dist/ssap-ui-kit.cjs.development.css';
import { Web3ReactProvider } from '@web3-react/core';
import { ThemeProvider } from 'ssap-ui-kit';
import { ethers } from 'ethers';
import React from 'react';
import 'react-app-polyfill/stable';
import ReactDOM from 'react-dom';
import TagManager from 'react-gtm-module';
import Modal from 'react-modal';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import App from './App';
import ConnectWalletModal from './components/ConnectWalletModal';
import ErrorBoundary from './components/ErrorBoundary';
import StaticPoolDataProviderWrapper from './components/PoolDataProviderWrapper';
import { UnlockWalletPreloader } from './components/UnlockWalletPreloader';
import globalStyle from './globalStyle';
import {
  getDefaultChainId,
  getSupportedChainIds,
} from './helpers/config/markets-and-network-config';
import { IPFS_MODE } from './helpers/config/misc-config';
import WrappedApolloProvider from './libs/apollo-config';
import { ConnectionStatusProvider } from './libs/connection-status-provider';
import { LanguageProvider } from './libs/language-provider';
import { MenuProvider } from './libs/menu';
import { DynamicPoolDataProvider } from './libs/pool-data-provider';
import { IncentivesDataProvider } from './libs/pool-data-provider/hooks/use-incentives-data-context';
import { ProtocolDataProvider } from './libs/protocol-data-provider';
import { ReferralHandler } from './libs/referral-handler';
import initSentry from './libs/sentry';
import { TxBuilderProvider } from './libs/tx-provider';
import { PermissionProvider } from './libs/use-permissions/usePermissions';
import { WalletBalanceProvider } from './libs/wallet-balance-provider/WalletBalanceProvider';
import { Web3Provider } from './libs/web3-data-provider';
import * as serviceWorker from './serviceWorker';

initSentry();
Modal.setAppElement('#root');

const GTM_ID = process.env.REACT_APP_GTM_ID;
if (GTM_ID) {
  TagManager.initialize({ gtmId: GTM_ID });
}

function getWeb3Library(provider: any): ethers.providers.Web3Provider {
  return new ethers.providers.Web3Provider(provider);
}

const Router = ({ children }: React.PropsWithChildren<{}>) =>
  IPFS_MODE ? <HashRouter>{children}</HashRouter> : <BrowserRouter>{children}</BrowserRouter>;

ReactDOM.render(
  <div className="Main">
    <Router>
      <ReferralHandler>
        <LanguageProvider>
          <ThemeProvider>
            <ProtocolDataProvider>
              <WrappedApolloProvider>
                <ConnectionStatusProvider>
                  <MenuProvider>
                    <Web3ReactProvider getLibrary={getWeb3Library}>
                      <ErrorBoundary>
                        <Web3Provider
                          defaultChainId={getDefaultChainId()}
                          supportedChainIds={getSupportedChainIds()}
                          preloader={UnlockWalletPreloader}
                          connectWalletModal={ConnectWalletModal}
                        >
                          <PermissionProvider>
                            <WalletBalanceProvider>
                              <StaticPoolDataProviderWrapper>
                                <DynamicPoolDataProvider>
                                  <IncentivesDataProvider>
                                    <TxBuilderProvider>
                                      <App />
                                    </TxBuilderProvider>
                                  </IncentivesDataProvider>
                                </DynamicPoolDataProvider>
                              </StaticPoolDataProviderWrapper>
                            </WalletBalanceProvider>
                          </PermissionProvider>
                        </Web3Provider>
                      </ErrorBoundary>
                    </Web3ReactProvider>
                  </MenuProvider>
                </ConnectionStatusProvider>
              </WrappedApolloProvider>
            </ProtocolDataProvider>
          </ThemeProvider>
        </LanguageProvider>
      </ReferralHandler>
    </Router>

    <style jsx={true} global={true}>
      {globalStyle}
    </style>
  </div>,
  document.getElementById('root')
);

serviceWorker.unregister();
